import React, { useEffect, useMemo, useState } from 'react';
import type { PeriodFilter } from '@/components/wyscout/MatchEvents/types';
import EventFilters from './wyscout-events/EventFilters';
import EventPitch from './wyscout-events/EventPitch';
import EventTimeline from './wyscout-events/EventTimeline';
import { eventCategoryMatches, goalBeneficiaryTeamId, sortEventsChronologically } from './wyscout-events/helpers';
import type { EventCategory, EventTeamFilter, WyscoutEventsAnalysisProps } from './wyscout-events/types';

const periodOrder: PeriodFilter[] = ['1H', '2H', 'E1', 'E2', 'P'];

const WyscoutEventsAnalysis: React.FC<WyscoutEventsAnalysisProps> = ({
  events,
  matchId,
  canWatchVideo,
  requestEventVideo,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  onViewGoalBuildUp,
  initialCategory = 'key',
  emphasizedEventIds,
  eventRelationshipLabels,
}) => {
  const [category, setCategory] = useState<EventCategory>(initialCategory);
  const [team, setTeam] = useState<EventTeamFilter>('all');
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const availablePeriods = useMemo(() => periodOrder.filter(value => events.some(event => event.matchPeriod === value)), [events]);
  const filteredEvents = useMemo(() => sortEventsChronologically(events.filter(event => {
    if (!eventCategoryMatches(event, category)) return false;
    if (period !== 'all' && event.matchPeriod !== period) return false;
    if (team === 'home' && event.team?.id !== homeTeamId) return false;
    if (team === 'away' && event.team?.id !== awayTeamId) return false;
    return true;
  })), [awayTeamId, category, events, homeTeamId, period, team]);

  useEffect(() => {
    if (period !== 'all' && !availablePeriods.includes(period)) setPeriod('all');
  }, [availablePeriods, period]);

  useEffect(() => {
    if (!filteredEvents.some(event => event.id === selectedEventId)) {
      setSelectedEventId(filteredEvents[0]?.id ?? null);
    }
  }, [filteredEvents, selectedEventId]);

  const viewGoalBuildUp = onViewGoalBuildUp ? (event: typeof events[number]): void => {
    const teamId = goalBeneficiaryTeamId(event, homeTeamId, awayTeamId);
    if (teamId !== null) onViewGoalBuildUp({ minute: event.minute, teamId });
  } : undefined;

  if (events.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-900">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">No match events available</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Events will appear here when Wyscout data is available.</p>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <EventFilters
        category={category}
        team={team}
        period={period}
        availablePeriods={availablePeriods}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        onCategoryChange={setCategory}
        onTeamChange={setTeam}
        onPeriodChange={setPeriod}
      />

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)] lg:items-start">
        <EventPitch
          events={filteredEvents}
          homeTeamId={homeTeamId}
          awayTeamId={awayTeamId}
          homeTeamName={homeTeamName}
          awayTeamName={awayTeamName}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
        />
        <EventTimeline
          events={filteredEvents}
          matchId={matchId}
          canWatchVideo={canWatchVideo}
          requestEventVideo={requestEventVideo}
          homeTeamId={homeTeamId}
          awayTeamId={awayTeamId}
          homeTeamName={homeTeamName}
          awayTeamName={awayTeamName}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          onViewGoalBuildUp={viewGoalBuildUp}
          emphasizedEventIds={emphasizedEventIds}
          eventRelationshipLabels={eventRelationshipLabels}
        />
      </div>
    </div>
  );
};

export default WyscoutEventsAnalysis;
